import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixAssetsV1SiteScript from './assets-v1-site-script-scripts.http.js';

export interface SiteScript {
  /**
   * Position where the script is placed on the page.
   * @readonly
   */
  position?: PositionOnPageWithLiterals;
  /**
   * Script HTML
   * @readonly
   * @maxLength 15000
   */
  html?: string;
}

export enum PositionOnPage {
  UNKNOWN_POSITION = 'UNKNOWN_POSITION',
  /** HEAD position */
  HEAD = 'HEAD',
  /** BODY_START position */
  BODY_START = 'BODY_START',
  /** BODY_END position */
  BODY_END = 'BODY_END',
}

/** @enumType */
export type PositionOnPageWithLiterals =
  | PositionOnPage
  | 'UNKNOWN_POSITION'
  | 'HEAD'
  | 'BODY_START'
  | 'BODY_END';

export interface ListSiteScriptsRequest {
  /**
   * Full canonical URL of the page where SEO tags should be resolved.
   * When provided together with `page_name`, the response includes SEO tags
   * for the page as additional HEAD-position site scripts.
   * @maxLength 2048
   */
  pageUrl?: string | null;
  /**
   * Page name identifier matching the page in the Wix site structure.
   * Required together with `page_url` to resolve SEO tags.
   * @maxLength 255
   */
  pageName?: string | null;
}

export interface ListSiteScriptsResponse {
  /** List of site scripts. */
  siteScripts?: SiteScript[];
}

export interface GetEssentialPropertiesRequest {}

export interface GetEssentialPropertiesResponse {
  /** Properties of the site */
  properties?: EssentialProperties;
}

export interface EssentialProperties {
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   * @format LANGUAGE
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @format CURRENCY
   */
  paymentCurrency?: string | null;
  /**
   * Timezone in `America/New_York` format.
   * @maxLength 50
   */
  timeZone?: string | null;
  /** Multilingual settings. */
  multilingual?: Multilingual;
}

export interface Locale {
  /**
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   * @format LANGUAGE
   */
  languageCode?: string;
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   */
  country?: string;
}

export interface Multilingual {
  /** Auto redirect. */
  autoRedirect?: boolean;
  /**
   * Supported languages.
   * @maxSize 100
   */
  supportedLanguages?: SupportedLanguage[];
}

export interface SupportedLanguage {
  /**
   * Language code.
   * @format LANGUAGE
   */
  languageCode?: string;
  /**
   * Country code.
   * @format COUNTRY
   */
  countryCode?: string;
  /** Is primary language. */
  isPrimary?: boolean;
  /** Is visitor primary language. */
  isVisitorPrimary?: boolean;
  /** Locale. */
  locale?: Locale;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface GetSiteMetadataRequest {
  /**
   * Full canonical URL of the page where SEO tags should be resolved.
   * When provided together with `page_name`, the response includes SEO tags
   * for the page as additional HEAD-position site scripts.
   * @maxLength 2048
   */
  pageUrl?: string | null;
  /**
   * Page name identifier matching the page in the Wix site structure.
   * Required together with `page_url` to resolve SEO tags.
   * @maxLength 255
   */
  pageName?: string | null;
}

export interface GetSiteMetadataResponse {
  /** List of site scripts. */
  siteScripts?: SiteScript[];
  /** Essential properties of the site. */
  properties?: EssentialProperties;
}

/**
 * Retrieves a list of up to 100 site scripts
 * @public
 * @documentationMaturity preview
 * @permissionId SITE_SCRIPTS.READ
 * @fqn wix.assets.scripts.v1.SiteScriptsService.ListSiteScripts
 */
export async function listSiteScripts(
  options?: ListSiteScriptsOptions
): Promise<
  NonNullablePaths<
    ListSiteScriptsResponse,
    | `siteScripts`
    | `siteScripts.${number}.position`
    | `siteScripts.${number}.html`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageUrl: options?.pageUrl,
    pageName: options?.pageName,
  });

  const reqOpts = ambassadorWixAssetsV1SiteScript.listSiteScripts(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pageUrl: '$[0].pageUrl',
          pageName: '$[0].pageName',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListSiteScriptsOptions {
  /**
   * Full canonical URL of the page where SEO tags should be resolved.
   * When provided together with `page_name`, the response includes SEO tags
   * for the page as additional HEAD-position site scripts.
   * @maxLength 2048
   */
  pageUrl?: string | null;
  /**
   * Page name identifier matching the page in the Wix site structure.
   * Required together with `page_url` to resolve SEO tags.
   * @maxLength 255
   */
  pageName?: string | null;
}

/**
 * Retrieves the essential properties of the site
 * @public
 * @documentationMaturity preview
 * @permissionId SITE_SCRIPTS.READ
 * @fqn wix.assets.scripts.v1.SiteScriptsService.GetEssentialProperties
 */
export async function getEssentialProperties(): Promise<
  NonNullablePaths<
    GetEssentialPropertiesResponse,
    | `properties.locale.languageCode`
    | `properties.locale.country`
    | `properties.multilingual.autoRedirect`
    | `properties.multilingual.supportedLanguages`
    | `properties.multilingual.supportedLanguages.${number}.languageCode`
    | `properties.multilingual.supportedLanguages.${number}.countryCode`
    | `properties.multilingual.supportedLanguages.${number}.isPrimary`
    | `properties.multilingual.supportedLanguages.${number}.isVisitorPrimary`
    | `properties.multilingual.supportedLanguages.${number}.resolutionMethod`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixAssetsV1SiteScript.getEssentialProperties(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves site metadata including both site scripts and essential properties
 * @public
 * @documentationMaturity preview
 * @permissionId SITE_SCRIPTS.READ
 * @fqn wix.assets.scripts.v1.SiteScriptsService.GetSiteMetadata
 */
export async function getSiteMetadata(
  options?: GetSiteMetadataOptions
): Promise<
  NonNullablePaths<
    GetSiteMetadataResponse,
    | `siteScripts`
    | `siteScripts.${number}.position`
    | `siteScripts.${number}.html`
    | `properties.locale.languageCode`
    | `properties.locale.country`
    | `properties.multilingual.autoRedirect`
    | `properties.multilingual.supportedLanguages`
    | `properties.multilingual.supportedLanguages.${number}.languageCode`
    | `properties.multilingual.supportedLanguages.${number}.countryCode`
    | `properties.multilingual.supportedLanguages.${number}.isPrimary`
    | `properties.multilingual.supportedLanguages.${number}.isVisitorPrimary`
    | `properties.multilingual.supportedLanguages.${number}.resolutionMethod`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pageUrl: options?.pageUrl,
    pageName: options?.pageName,
  });

  const reqOpts = ambassadorWixAssetsV1SiteScript.getSiteMetadata(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pageUrl: '$[0].pageUrl',
          pageName: '$[0].pageName',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSiteMetadataOptions {
  /**
   * Full canonical URL of the page where SEO tags should be resolved.
   * When provided together with `page_name`, the response includes SEO tags
   * for the page as additional HEAD-position site scripts.
   * @maxLength 2048
   */
  pageUrl?: string | null;
  /**
   * Page name identifier matching the page in the Wix site structure.
   * Required together with `page_url` to resolve SEO tags.
   * @maxLength 255
   */
  pageName?: string | null;
}
